import type { TableSort } from '@shared/components/data-table/data-table';
import { hubFetch } from '@core/auth/services/hub-fetch';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { Auth } from '@core/auth/services/auth';
import {
  Address,
  CreateAddressRequest,
  UpdateAddressRequest,
  AddressListResponse,
} from '@shared/models/address.model';

/**
 * Rows a picker loads for browsing. The endpoint defaults to 100 and caps at 50 000;
 * anything past this page is reached through server-side search, not by loading more.
 */
export const ADDRESS_BROWSE_LIMIT = 100;

@Injectable({
  providedIn: 'root',
})
export class AddressService {
  private readonly apiUrl = `${environment.apiUrl}`;
  private readonly auth = inject(Auth);

  // Cache for addresses per organization
  private readonly addressCache = new Map<string, Address[]>();
  private readonly loadingStates = new Map<string, Promise<Address[]>>();
  /** Total address count per organization, from the last browse fetch's meta. */
  private readonly totalCounts = new Map<string, number>();

  /**
   * Get authorization headers for API requests
   */
  private getAuthHeaders(): HeadersInit {

    return {
      'Content-Type': 'application/json',

    };
  }

  /**
   * Handle API errors and transform to user-friendly messages
   */
  private async handleApiError(response: Response): Promise<never> {
    let errorMessage = 'An unexpected error occurred';

    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // If JSON parsing fails, use status text
      errorMessage = response.statusText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  /**
   * Generic fetch wrapper with error handling
   */
  private async fetchApi<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = endpoint.startsWith('http')
      ? endpoint
      : `${this.apiUrl}${endpoint}`;

    const response = await hubFetch(url, {
      headers: this.getAuthHeaders(),
      ...options,
    });

    if (!response.ok) {
      await this.handleApiError(response);
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  /**
   * Fetch one page of addresses from the server (no cache).
   * Returns the data array and pagination meta.
   */
  async getPaginatedAddresses(
    organizationId: string,
    page = 1,
    limit = 20,
    sort: TableSort | null = null,
  ): Promise<{ data: Address[]; meta: { total: number; page: number; pages: number } }> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (sort) { params.set('sortBy', sort.key); params.set('sortDirection', sort.direction); }
    return this.fetchApi<{ data: Address[]; meta: { total: number; page: number; pages: number } }>(
      `/organizations/${organizationId}/addresses?${params}`,
    );
  }

  /**
   * Get all addresses for the current organization with caching
   */
  async getAddressesByOrganization(
    organizationId: string,
    forceRefresh = false
  ): Promise<Address[]> {
    // Check cache first unless force refresh
    if (!forceRefresh && this.addressCache.has(organizationId)) {
      console.log(
        '[AddressService] Returning cached addresses for:',
        organizationId
      );
      return this.addressCache.get(organizationId)!;
    }

    // Check if already loading
    if (this.loadingStates.has(organizationId)) {
      console.log(
        '[AddressService] Already loading addresses for:',
        organizationId
      );
      return this.loadingStates.get(organizationId)!;
    }

    // Create loading promise
    const loadingPromise = this.loadAddressesFromApi(organizationId);
    this.loadingStates.set(organizationId, loadingPromise);

    try {
      const addresses = await loadingPromise;
      this.addressCache.set(organizationId, addresses);
      return addresses;
    } finally {
      this.loadingStates.delete(organizationId);
    }
  }

  /**
   * Internal method to load addresses from API
   */
  private async loadAddressesFromApi(
    organizationId: string
  ): Promise<Address[]> {
    console.log('[AddressService] Starting API request for:', organizationId);
    const response = await this.fetchApi<{
      data: Address[];
      meta: { total: number };
    }>(
      `/organizations/${organizationId}/addresses?limit=${ADDRESS_BROWSE_LIMIT}`
    );
    const addresses = response.data;
    this.totalCounts.set(organizationId, response.meta?.total ?? addresses.length);
    console.log(
      '[AddressService] API response received:',
      addresses.length,
      'of',
      this.totalCounts.get(organizationId),
      'addresses'
    );
    return addresses;
  }

  /**
   * How many addresses the organization has in total, as reported by the last
   * browse fetch — lets a picker say how many rows its capped list is hiding.
   * Undefined until `getAddressesByOrganization` has run once.
   */
  getTotalCount(organizationId: string): number | undefined {
    return this.totalCounts.get(organizationId);
  }

  /**
   * What an address picker shows: the cached browse page when nothing is typed,
   * server-side matches otherwise. Searching client-side over the browse page alone
   * would never find anything past ADDRESS_BROWSE_LIMIT.
   */
  async findForPicker(
    organizationId: string,
    searchTerm: string,
  ): Promise<Address[]> {
    const term = searchTerm.trim();
    return term
      ? this.searchAddresses(organizationId, term)
      : this.getAddressesByOrganization(organizationId);
  }

  /**
   * Get address by ID
   */
  async getAddressById(id: string): Promise<Address> {
    return this.fetchApi<Address>(`/addresses/${id}`);
  }

  /**
   * Create a new address
   */
  async createAddress(
    organizationId: string,
    data: CreateAddressRequest
  ): Promise<Address> {
    const newAddress = await this.fetchApi<Address>(
      `/organizations/${organizationId}/addresses`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );

    // Update cache with new address
    this.addToCache(organizationId, newAddress);

    return newAddress;
  }

  /**
   * Update an existing address
   */
  async updateAddress(
    organizationId: string,
    addressId: string,
    data: UpdateAddressRequest
  ): Promise<Address> {
    const updatedAddress = await this.fetchApi<Address>(
      `/organizations/${organizationId}/addresses/${addressId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    );

    // Update cache
    this.updateInCache(updatedAddress);

    return updatedAddress;
  }

  /**
   * Delete an address
   */
  async deleteAddress(
    organizationId: string,
    addressId: string
  ): Promise<void> {
    await this.fetchApi<void>(
      `/organizations/${organizationId}/addresses/${addressId}`,
      {
        method: 'DELETE',
      }
    );

    // Remove from cache
    this.removeFromCache(addressId);
  }

  /**
   * Cache management methods
   */

  /**
   * Add address to cache
   */
  private addToCache(organizationId: string, address: Address): void {
    const cachedAddresses = this.addressCache.get(organizationId);
    if (cachedAddresses) {
      cachedAddresses.push(address);
    }
  }

  /**
   * Update address in cache
   */
  private updateInCache(updatedAddress: Address): void {
    for (const [orgId, addresses] of this.addressCache) {
      const index = addresses.findIndex(
        (address) => address.addressId === updatedAddress.addressId
      );
      if (index !== -1) {
        addresses[index] = updatedAddress;
        break;
      }
    }
  }

  /**
   * Remove address from cache
   */
  private removeFromCache(addressId: string): void {
    for (const [orgId, addresses] of this.addressCache) {
      const index = addresses.findIndex(
        (address) => address.addressId === addressId
      );
      if (index !== -1) {
        addresses.splice(index, 1);
        break;
      }
    }
  }

  /**
   * Clear cache for specific organization
   */
  clearCacheForOrganization(organizationId: string): void {
    this.addressCache.delete(organizationId);
  }

  /**
   * Clear entire address cache
   */
  clearCache(): void {
    this.addressCache.clear();
  }

  /**
   * Search addresses by text (street, city, etc.)
   */
  async searchAddresses(
    organizationId: string,
    searchTerm: string,
    limit = ADDRESS_BROWSE_LIMIT,
  ): Promise<Address[]> {
    const response = await this.fetchApi<{ data: Address[] }>(
      `/organizations/${organizationId}/addresses` +
        `?search=${encodeURIComponent(searchTerm)}&limit=${limit}`
    );
    return response.data;
  }
}
